import type {
  ManagerialDashboardCampaignOption,
  ManagerialDashboardFilterOption,
} from '@/types/api'

export type ExecutivePeriodId = '12m' | '30d' | '7d' | 'month'
export type ExecutiveResourceId = 'all' | string
export type ExecutiveStatusId = 'all' | 'paid' | 'pending' | 'canceled' | 'expired'

export type ExecutiveFilterOption<Id extends string> = {
  id: Id
  label: string
}

export type ExecutiveFiltersState = {
  period: ExecutivePeriodId
  institution: ExecutiveResourceId
  campaign: ExecutiveResourceId
  status: ExecutiveStatusId
}

export const DEFAULT_EXECUTIVE_FILTERS: ExecutiveFiltersState = {
  period: '12m',
  institution: 'all',
  campaign: 'all',
  status: 'all',
}

export const EXECUTIVE_PERIOD_OPTIONS: ReadonlyArray<ExecutiveFilterOption<ExecutivePeriodId>> = [
  { id: '12m', label: 'Últimos 12 meses' },
  { id: '30d', label: 'Últimos 30 dias' },
  { id: '7d', label: 'Últimos 7 dias' },
  { id: 'month', label: 'Mês atual' },
]

export const ALL_INSTITUTIONS_OPTION: ExecutiveFilterOption<'all'> = {
  id: 'all',
  label: 'Todas as instituições',
}

export const ALL_CAMPAIGNS_OPTION: ExecutiveFilterOption<'all'> = {
  id: 'all',
  label: 'Todas as campanhas',
}

export const EXECUTIVE_STATUS_OPTIONS: ReadonlyArray<ExecutiveFilterOption<ExecutiveStatusId>> = [
  { id: 'all', label: 'Todos os status' },
  { id: 'paid', label: 'Pago' },
  { id: 'pending', label: 'Pendente' },
  { id: 'canceled', label: 'Cancelado' },
  { id: 'expired', label: 'Expirado' },
]

const RESOURCE_ID_PATTERN = /^\d+$/

export function isExecutivePeriodId(value: string): value is ExecutivePeriodId {
  return EXECUTIVE_PERIOD_OPTIONS.some((option) => option.id === value)
}

export function isExecutiveResourceId(value: string): value is ExecutiveResourceId {
  return value === 'all' || RESOURCE_ID_PATTERN.test(value)
}

export function isExecutiveInstitutionId(value: string): value is ExecutiveResourceId {
  return isExecutiveResourceId(value)
}

export function isExecutiveCampaignId(value: string): value is ExecutiveResourceId {
  return isExecutiveResourceId(value)
}

export function isExecutiveStatusId(value: string): value is ExecutiveStatusId {
  return EXECUTIVE_STATUS_OPTIONS.some((option) => option.id === value)
}

export function parseExecutiveResourceId(value: ExecutiveResourceId): number | null {
  if (value === 'all' || !RESOURCE_ID_PATTERN.test(value)) {
    return null
  }
  return Number(value)
}

export function buildExecutiveInstitutionOptions(
  institutions: ManagerialDashboardFilterOption[]
): ExecutiveFilterOption<string>[] {
  return [
    ALL_INSTITUTIONS_OPTION,
    ...institutions.map((institution) => ({ id: String(institution.id), label: institution.label })),
  ]
}

export function buildExecutiveCampaignOptions(
  campaigns: ManagerialDashboardCampaignOption[]
): ExecutiveFilterOption<string>[] {
  return [
    ALL_CAMPAIGNS_OPTION,
    ...campaigns.map((campaign) => ({ id: String(campaign.id), label: campaign.label })),
  ]
}

export function normalizeExecutiveSearchText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
}

export function filterExecutiveOptionsByQuery<Id extends string>(
  options: ReadonlyArray<ExecutiveFilterOption<Id>>,
  query: string
): ExecutiveFilterOption<Id>[] {
  const needle = normalizeExecutiveSearchText(query)
  if (needle === '') {
    return [...options]
  }

  return options.filter((option) => normalizeExecutiveSearchText(option.label).includes(needle))
}
